import React, { useRef, useState, useCallback, useEffect } from 'react';
import { getStroke } from 'perfect-freehand';
import '../styles/drawing.css';

// Get API URL from environment variable, fallback to localhost for development
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Convert stroke points into SVG path
function getSvgPathFromStroke(strokePoints) {
  if (!strokePoints.length) return "";
  return strokePoints.reduce(
    (acc, [x, y], i) =>
      i === 0 ? `M ${x.toFixed(2)} ${y.toFixed(2)}` : `${acc} L ${x.toFixed(2)} ${y.toFixed(2)}`,
    ""
  );
}

const Drawing = ({ username, onLogout }) => {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const pointsRef = useRef([]);
  const rafRef = useRef(null);
  const pendingPointsRef = useRef([]);

  // Stroke state
  const [points, setPoints] = useState([]);
  const [completedStrokes, setCompletedStrokes] = useState([]);
  const [currentPathData, setCurrentPathData] = useState("");

  // Stroke parameters - matching sketch.jsx exactly
  const [strokeThickness, setStrokeThickness] = useState(2.0);
  const [thinningValue, setThinningValue] = useState(0);
  const [smoothingValue, setSmoothingValue] = useState(0.5);
  const [streamlineValue, setStreamlineValue] = useState(0.5);
  const [strokeColor, setStrokeColor] = useState(1.0); // Grayscale: 0 = white, 1 = black
  const [strokeOpacity, setStrokeOpacity] = useState(1.0); // Opacity: 0 = transparent, 1 = opaque

  const [strokeParameters, setStrokeParameters] = useState({
    size: strokeThickness,
    thinning: thinningValue,
    smoothing: smoothingValue,
    streamline: streamlineValue,
    simulatePressure: false
  });

  // Convert grayscale value (0-1) to hex color
  const grayscaleToHex = useCallback((value) => {
    // Clamp value between 0 and 1
    const clamped = Math.max(0, Math.min(1, value));
    // Convert to 0-255 range (inverted: 0 = white, 1 = black)
    const gray = Math.round((1 - clamped) * 255);
    // Return hex color
    const hex = gray.toString(16).padStart(2, '0');
    return `#${hex}${hex}${hex}`;
  }, []);

  // Effect to update strokeParameters when any related value changes
  useEffect(() => {
    setStrokeParameters(prevParams => ({
      ...prevParams,
      size: strokeThickness,
      thinning: thinningValue,
      smoothing: smoothingValue,
      streamline: streamlineValue,
    }));
  }, [strokeThickness, thinningValue, smoothingValue, streamlineValue]);

  // Mode state: "record" or "classify"
  const [mode, setMode] = useState("record");

  // Category checkboxes (for Record Mode)
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sessionStrokeCount, setSessionStrokeCount] = useState(0); // Session-based counter (resets on category change)
  
  // Stroke counts for all categories (total in database)
  const [categoryCounts, setCategoryCounts] = useState({
    constraining: 0,
    defining: 0,
    detailing: 0,
    hatches: 0
  });

  // Classify Mode states
  const [selectedDLModels, setSelectedDLModels] = useState([]);
  const [selectedMLModels, setSelectedMLModels] = useState([]);
  const [classificationResults, setClassificationResults] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [isClassifying, setIsClassifying] = useState(false);

  // Loading states for operations
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');

  // Get coordinates relative to canvas
  const getCanvasCoordinates = useCallback((clientX, clientY) => {
    if (!canvasRef.current) return [0, 0];
    const rect = canvasRef.current.getBoundingClientRect();
    return [clientX - rect.left, clientY - rect.top];
  }, []);

  // Note: Removed updateStrokeCount - now using session-based counter instead

  // Fetch stroke counts for all categories (for display in checkboxes)
  const updateAllCategoryCounts = useCallback(async () => {
    const categories = ['constraining', 'defining', 'detailing', 'hatches'];
    try {
      const counts = {};
      await Promise.all(
        categories.map(async (category) => {
          try {
            const response = await fetch(`${API_URL}/api/strokes/count?username=${username}&category=${category}`);
            const data = await response.json();
            if (data.status === 'success') {
              counts[category] = data.count;
            } else {
              counts[category] = 0;
            }
          } catch (error) {
            console.error(`Error fetching count for ${category}:`, error);
            counts[category] = 0;
          }
        })
      );
      setCategoryCounts(counts);
    } catch (error) {
      console.error('Error fetching category counts:', error);
    }
  }, [username]);

  // Fetch all category counts on mount
  useEffect(() => {
    updateAllCategoryCounts();
  }, [updateAllCategoryCounts]);

  // Handle mode change
  const handleModeChange = (newMode) => {
    if (newMode !== mode) {
      // Clear canvas when switching modes
      handleClear();
      setMode(newMode);
      
      // Reset mode-specific states
      if (newMode === 'record') {
        setSelectedDLModels([]);
        setSelectedMLModels([]);
        setClassificationResults(null);
        setShowResultsModal(false);
      } else if (newMode === 'classify') {
        setSelectedCategory(null);
        setSessionStrokeCount(0);
      }
    }
  };

  // Handle DL model selection (Classify Mode)
  const toggleDLModel = (modelName) => {
    setSelectedDLModels(prev => 
      prev.includes(modelName) 
        ? prev.filter(m => m !== modelName)
        : [...prev, modelName]
    );
  };

  // Handle ML model selection (Classify Mode)
  const toggleMLModel = (modelName) => {
    setSelectedMLModels(prev => 
      prev.includes(modelName) 
        ? prev.filter(m => m !== modelName)
        : [...prev, modelName]
    );
  };

  // Reset model selections (Classify Mode)
  const handleResetModels = () => {
    setSelectedDLModels([]);
    setSelectedMLModels([]);
    setClassificationResults(null);
    setShowResultsModal(false);
  };

  // Classify the last stroke (Classify Mode)
  const handleClassify = async () => {
    // Get the last stroke (either from completedStrokes or current points)
    let lastStroke = null;
    
    if (completedStrokes.length > 0) {
      lastStroke = completedStrokes[completedStrokes.length - 1];
    } else if (points.length > 0) {
      // Current stroke being drawn
      const strokePath = getStroke(points, strokeParameters);
      const path = getSvgPathFromStroke(strokePath);
      const colorHex = grayscaleToHex(strokeColor);
      
      lastStroke = {
        rawPoints: points,
        strokeParameters: { ...strokeParameters },
        color: colorHex,
        opacity: strokeOpacity
      };
    }
    
    if (!lastStroke) {
      alert('No stroke to classify. Please draw a stroke first.');
      return;
    }
    
    if (selectedDLModels.length === 0 && selectedMLModels.length === 0) {
      alert('Please select at least one model to classify with.');
      return;
    }
    
    setIsClassifying(true);
    setProcessingMessage('Classifying stroke...');
    
    try {
      // Prepare stroke data
      const rawPoints = lastStroke.rawPoints;
      const x_coordinates = rawPoints.map(p => p[0]);
      const y_coordinates = rawPoints.map(p => p[1]);
      const pressure_values = rawPoints.map(p => p[2] || 0.5);
      
      const strokeData = {
        x_coordinates,
        y_coordinates,
        pressure_values,
        thickness_values: new Array(rawPoints.length).fill(lastStroke.strokeParameters.size),
        color: lastStroke.color,
        grayscale_value: strokeColor,
        opacity: lastStroke.opacity || strokeOpacity,
        stroke_parameters: lastStroke.strokeParameters
      };
      
      // Send to backend for classification
      const response = await fetch(`${API_URL}/api/classify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stroke_data: strokeData,
          dl_models: selectedDLModels,
          ml_models: selectedMLModels
        }),
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        setClassificationResults({
          results: data.results,
          image_base64: data.image_base64,
          stroke_data: strokeData
        });
        setShowResultsModal(true);
      } else {
        alert(`Classification failed: ${data.message}`);
      }
    } catch (error) {
      console.error('Classification error:', error);
      alert('Failed to classify stroke. Please make sure the backend is running.');
    } finally {
      setIsClassifying(false);
    }
  };

  // Download classification results as ZIP
  const handleDownloadResults = async () => {
    if (!classificationResults) return;
    
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      // Add image
      if (classificationResults.image_base64) {
        const imageData = atob(classificationResults.image_base64);
        const imageArray = new Uint8Array(imageData.length);
        for (let i = 0; i < imageData.length; i++) {
          imageArray[i] = imageData.charCodeAt(i);
        }
        zip.file('stroke_image.png', imageArray);
      }
      
      // Add JSON
      const jsonContent = JSON.stringify(classificationResults.stroke_data, null, 2);
      zip.file('stroke_data.json', jsonContent);
      
      // Add results as Markdown
      let markdown = '# Stroke Classification Results\n\n';
      markdown += `**Date**: ${new Date().toLocaleString()}\n\n`;
      markdown += '## Model Predictions\n\n';
      
      const results = classificationResults.results;
      Object.keys(results).forEach(modelName => {
        const result = results[modelName];
        markdown += `### ${modelName}\n\n`;
        markdown += `- **Prediction**: ${result.prediction}\n`;
        markdown += `- **Confidence**: ${(result.confidence * 100).toFixed(2)}%\n\n`;
        
        if (result.probabilities && Object.keys(result.probabilities).length > 0) {
          markdown += '**All Class Probabilities**:\n\n';
          Object.entries(result.probabilities).forEach(([className, prob]) => {
            markdown += `  - ${className}: ${(prob * 100).toFixed(2)}%\n`;
          });
          markdown += '\n';
        }
      });
      
      zip.file('classification_results.md', markdown);
      
      // Generate and download ZIP
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stroke_classification_${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading results:', error);
      alert('Failed to download results');
    }
  };

  // Copy classification results to clipboard
  const handleCopyResults = () => {
    if (!classificationResults) return;
    
    let text = 'Stroke Classification Results\n';
    text += '='.repeat(40) + '\n\n';
    
    const results = classificationResults.results;
    Object.keys(results).forEach(modelName => {
      const result = results[modelName];
      text += `${modelName}:\n`;
      text += `  Prediction: ${result.prediction}\n`;
      text += `  Confidence: ${(result.confidence * 100).toFixed(2)}%\n\n`;
    });
    
    navigator.clipboard.writeText(text).then(() => {
      alert('Results copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy results');
    });
  };

  // Handle category checkbox change
  const handleCategoryChange = (category) => {
    if (selectedCategory === category) {
      // Unchecking - clear selection and reset session counter
      setSelectedCategory(null);
      setSessionStrokeCount(0);
    } else {
      // Checking a new category - reset session counter to 0
      setSelectedCategory(category);
      setSessionStrokeCount(0);
    }
  };

  // Save stroke to backend
  const saveStroke = useCallback(async (rawPoints, category) => {
    if (!rawPoints || rawPoints.length === 0) return;

    try {
      // Extract coordinates and pressure
      const x_coordinates = rawPoints.map(p => p[0]);
      const y_coordinates = rawPoints.map(p => p[1]);
      const pressure_values = rawPoints.map(p => p[2] || 0.5);

      // Generate stroke path
      const strokePath = getStroke(rawPoints, strokeParameters);
      const path = getSvgPathFromStroke(strokePath);

      // Convert grayscale to hex color
      const colorHex = grayscaleToHex(strokeColor);

      // Prepare stroke data
      const strokeData = {
        x_coordinates,
        y_coordinates,
        pressure_values,
        thickness_values: new Array(rawPoints.length).fill(strokeParameters.size),
        color: colorHex,
        grayscale_value: strokeColor,
        opacity: strokeOpacity,
        stroke_parameters: {
          size: strokeParameters.size,
          thinning: strokeParameters.thinning,
          smoothing: strokeParameters.smoothing,
          streamline: strokeParameters.streamline,
          simulatePressure: strokeParameters.simulatePressure
        },
        path
      };

      // Send to backend
      const response = await fetch(`${API_URL}/api/strokes/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          category,
          stroke_data: strokeData
        }),
      });

      const data = await response.json();
      if (data.status === 'success') {
        // Increment session counter
        setSessionStrokeCount(prev => prev + 1);
        // Update all category counts (database totals)
        await updateAllCategoryCounts();
        console.log('Stroke saved successfully');
      } else {
        console.error('Failed to save stroke:', data.message);
      }
    } catch (error) {
      console.error('Error saving stroke:', error);
    }
  }, [username, strokeParameters, strokeColor, strokeOpacity, grayscaleToHex, updateAllCategoryCounts]);

  // Handle pointer down
  const handlePointerDown = (e) => {
    if (e.buttons !== 1) return;
    e.target.setPointerCapture(e.pointerId);
    isDrawingRef.current = true;

    const [x, y] = getCanvasCoordinates(e.clientX, e.clientY);
    const pressure = e.pointerType === "mouse" ? 0.5 : Math.max(e.pressure || 0.5, 0.2);
    
    setPoints([[x, y, pressure]]);
    pointsRef.current = [[x, y, pressure]];
  };

  // Handle pointer move
  const handlePointerMove = (e) => {
    if (!isDrawingRef.current || e.buttons !== 1) return;

    const pressure = e.pointerType === "mouse" ? 0.5 : Math.max(e.pressure || 0.5, 0.2);
    const [x, y] = getCanvasCoordinates(e.clientX, e.clientY);

    pendingPointsRef.current.push([x, y, pressure]);

    if (rafRef.current) return;

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;

      if (pendingPointsRef.current.length > 0 && isDrawingRef.current) {
        const pointsToAdd = [...pendingPointsRef.current];
        pendingPointsRef.current = [];

        setPoints(prev => {
          const newPoints = [...prev, ...pointsToAdd];
          pointsRef.current = newPoints;
          return newPoints;
        });
      }
    });
  };

  // Handle pointer up
  const handlePointerUp = async (e) => {
    isDrawingRef.current = false;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const lastStrokeRawPoints = [...points, ...pendingPointsRef.current];
    pendingPointsRef.current = [];

    if (lastStrokeRawPoints.length === 0) {
      setPoints([]);
      setCurrentPathData("");
      return;
    }

    // If a category is selected, save the stroke and remove it from display
    if (selectedCategory) {
      await saveStroke(lastStrokeRawPoints, selectedCategory);
      // Stroke disappears (don't add to completedStrokes)
      setPoints([]);
      setCurrentPathData("");
    } else {
      // No category selected - keep stroke visible, don't save
      const strokePath = getStroke(lastStrokeRawPoints, strokeParameters);
      const path = getSvgPathFromStroke(strokePath);
      const colorHex = grayscaleToHex(strokeColor);
      
      setCompletedStrokes(prev => [...prev, {
        path,
        rawPoints: lastStrokeRawPoints,
        strokeParameters: { ...strokeParameters },
        color: colorHex,
        opacity: strokeOpacity
      }]);

      setTimeout(() => {
        setPoints([]);
        setCurrentPathData("");
      }, 0);
    }
  };

  // Clear all strokes
  const handleClear = () => {
    setCompletedStrokes([]);
    setPoints([]);
    setCurrentPathData("");
    // Reset session counter when clearing canvas
    setSessionStrokeCount(0);
  };

  // Delete all user data with double confirmation
  const handleDeleteData = async () => {
    // First confirmation
    const firstConfirm = window.confirm(
      `⚠️ WARNING: This will permanently delete ALL your stroke data (JSON files and images) for user "${username}".\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Click OK to continue, or Cancel to abort.`
    );

    if (!firstConfirm) return;

    // Second confirmation
    const secondConfirm = window.confirm(
      `🚨 FINAL WARNING: You are about to PERMANENTLY DELETE all data for "${username}".\n\n` +
      `This includes:\n` +
      `- All JSON stroke files\n` +
      `- All stroke images\n` +
      `- All categories (Constraining, Defining, Detailing, Hatches)\n\n` +
      `Type "DELETE" in the next prompt to confirm, or click Cancel to abort.`
    );

    if (!secondConfirm) return;

    // Third confirmation with text input
    const deleteText = window.prompt(
      `Type "DELETE" (in all caps) to confirm deletion:`
    );

    if (deleteText !== "DELETE") {
      alert("Deletion cancelled. Text did not match.");
      return;
    }

    // Show processing overlay
    setIsProcessing(true);
    setProcessingMessage('Deleting all data...');

    try {
      const response = await fetch(`${API_URL}/api/user/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        // Clear canvas and reset state
        handleClear();
        // Refresh all category counts
        await updateAllCategoryCounts();
        setSelectedCategory(null);
        setSessionStrokeCount(0);
        
        // Hide processing overlay before showing success
        setIsProcessing(false);
        alert(`✅ All data for "${username}" has been permanently deleted.`);
      } else {
        setIsProcessing(false);
        alert(`❌ Error: ${data.message || 'Failed to delete data'}`);
      }
    } catch (error) {
      console.error('Error deleting data:', error);
      setIsProcessing(false);
      alert('❌ Failed to connect to server. Please make sure the backend is running.');
    }
  };

  // Download all user data as zip
  const handleDownloadData = async () => {
    // Show processing overlay
    setIsProcessing(true);
    setProcessingMessage('Preparing download...');

    try {
      const response = await fetch(`${API_URL}/api/user/download?username=${username}`, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to download data');
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${username}_stroke_data.zip`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      // Hide processing overlay before showing success
      setIsProcessing(false);
      alert(`✅ Data for "${username}" has been downloaded successfully!`);
    } catch (error) {
      console.error('Error downloading data:', error);
      setIsProcessing(false);
      alert(`❌ Error: ${error.message || 'Failed to download data'}`);
    }
  };

  // Render current stroke preview
  useEffect(() => {
    if (points.length > 0) {
      const strokePath = getStroke(points, strokeParameters);
      const path = getSvgPathFromStroke(strokePath);
      setCurrentPathData(path);
    } else {
      setCurrentPathData("");
    }
  }, [points, strokeParameters]);

  // Get current stroke color
  const currentStrokeColor = grayscaleToHex(strokeColor);

  return (
    <div className="drawing-container">
      {/* Processing Overlay */}
      {isProcessing && (
        <div className="processing-overlay">
          <div className="processing-content">
            <div className="processing-spinner"></div>
            <p className="processing-message">{processingMessage}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="drawing-header">
        <div className="header-left">
          <div className="app-logo">
            <svg className="logo-icon" viewBox="0 0 40 40" fill="none">
              <path d="M5 20 Q 15 5, 25 20 T 35 20" stroke="#7FFF00" strokeWidth="3" fill="none"/>
              <path d="M8 25 L 32 25" stroke="#7FFF00" strokeWidth="2"/>
              <circle cx="20" cy="20" r="18" stroke="#7FFF00" strokeWidth="2" fill="none"/>
            </svg>
            <span className="app-name">AEGIS</span>
          </div>
          <div className="user-info">
            <span className="username-label">User:</span>
            <span className="username-value">{username}</span>
          </div>
        </div>
        
        {/* Mode Selector (Center) */}
        <div className="header-center">
          <div className="mode-selector">
            <select 
              value={mode} 
              onChange={(e) => handleModeChange(e.target.value)}
              className="mode-dropdown"
            >
              <option value="record">Record Mode</option>
              <option value="classify">Classify Mode</option>
            </select>
          </div>
        </div>
        
        <div className="header-right">
          {mode === 'record' && (
            <>
              <button className="header-download-button" onClick={handleDownloadData}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Download Data
              </button>
              <button className="header-delete-button" onClick={handleDeleteData}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Delete Data
              </button>
            </>
          )}
          {mode === 'classify' && (
            <>
              <button 
                className="header-classify-button" 
                onClick={handleClassify}
                disabled={isClassifying || (selectedDLModels.length === 0 && selectedMLModels.length === 0)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {isClassifying ? 'Classifying...' : 'Classify'}
              </button>
              <button className="header-reset-button" onClick={handleResetModels}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Reset
              </button>
            </>
          )}
          <button className="header-clear-button" onClick={handleClear}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Clear Canvas
          </button>
          <button className="header-logout-button" onClick={onLogout}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Logout
          </button>
        </div>
      </header>

      {/* Top Left Controls */}
      <div className="controls-panel">
        <div className="control-group">
          <label>Base Thickness</label>
          <input
            type="range"
            min="0.5"
            max="20"
            step="0.1"
            value={strokeThickness}
            onChange={(e) => setStrokeThickness(parseFloat(e.target.value))}
          />
          <span>{strokeThickness.toFixed(1)}</span>
        </div>

        <div className="control-group">
          <label>Pressure Sensitivity</label>
          <input
            type="range"
            min="-1.0"
            max="1.0"
            step="0.05"
            value={thinningValue}
            onChange={(e) => setThinningValue(parseFloat(e.target.value))}
          />
          <span>{thinningValue.toFixed(2)}</span>
        </div>

        <div className="control-group">
          <label>Smoothing</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={smoothingValue}
            onChange={(e) => setSmoothingValue(parseFloat(e.target.value))}
          />
          <span>{smoothingValue.toFixed(2)}</span>
        </div>

        <div className="control-group">
          <label>Streamline</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={streamlineValue}
            onChange={(e) => setStreamlineValue(parseFloat(e.target.value))}
          />
          <span>{streamlineValue.toFixed(2)}</span>
        </div>

        <div className="control-group">
          <label>Color (Grayscale)</label>
          <div className="color-slider-wrapper">
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={strokeColor}
              onChange={(e) => setStrokeColor(parseFloat(e.target.value))}
              className="color-slider"
            />
            <div className="color-preview" style={{ backgroundColor: currentStrokeColor }}></div>
          </div>
          <span>{strokeColor.toFixed(2)}</span>
        </div>

        <div className="control-group">
          <label>Opacity</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={strokeOpacity}
            onChange={(e) => setStrokeOpacity(parseFloat(e.target.value))}
          />
          <span>{strokeOpacity.toFixed(2)}</span>
        </div>
      </div>

      {/* Bottom Left Counter - Session-based */}
      {selectedCategory && (
        <div className="counter">
          Strokes: {sessionStrokeCount}
        </div>
      )}

      {/* Right Side Panel - Conditional based on mode */}
      {mode === 'record' && (
        <div className="category-panel">
          <div className="category-row">
            <div className="category-item">
              <input
                type="checkbox"
                id="constraining"
                checked={selectedCategory === 'constraining'}
                onChange={() => handleCategoryChange('constraining')}
              />
              <label htmlFor="constraining">
                Constraining <span style={{ color: '#7FFF00' }}>({categoryCounts.constraining})</span>
              </label>
            </div>

            <div className="category-item">
              <input
                type="checkbox"
                id="defining"
                checked={selectedCategory === 'defining'}
                onChange={() => handleCategoryChange('defining')}
              />
              <label htmlFor="defining">
                Defining <span style={{ color: '#7FFF00' }}>({categoryCounts.defining})</span>
              </label>
            </div>
          </div>

          <div className="category-row">
            <div className="category-item">
              <input
                type="checkbox"
                id="detailing"
                checked={selectedCategory === 'detailing'}
                onChange={() => handleCategoryChange('detailing')}
              />
              <label htmlFor="detailing">
                Detailing <span style={{ color: '#7FFF00' }}>({categoryCounts.detailing})</span>
              </label>
            </div>

            <div className="category-item">
              <input
                type="checkbox"
                id="hatches"
                checked={selectedCategory === 'hatches'}
                onChange={() => handleCategoryChange('hatches')}
              />
              <label htmlFor="hatches">
                Hatches/Shadows <span style={{ color: '#7FFF00' }}>({categoryCounts.hatches})</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {mode === 'classify' && (
        <div className="model-selection-panel">
          <div className="model-group">
            <h3 className="model-group-title">Deep Learning Models</h3>
            <div className="model-list">
              {['AlexNet', 'ConvNext', 'EfficientNet', 'ResNet50', 'ShuffleNet'].map(model => (
                <div key={model} className="model-item">
                  <input
                    type="checkbox"
                    id={`dl-${model}`}
                    checked={selectedDLModels.includes(model)}
                    onChange={() => toggleDLModel(model)}
                  />
                  <label htmlFor={`dl-${model}`}>{model}</label>
                </div>
              ))}
            </div>
          </div>

          <div className="model-group">
            <h3 className="model-group-title">Machine Learning Models</h3>
            <div className="model-list">
              {[
                'Logistic Regression',
                'SVM Linear',
                'SVM RBF',
                'SVM Poly',
                'Decision Tree',
                'Random Forest',
                'kNN',
                'Gaussian NB',
                'AdaBoost',
                'XGBoost'
              ].map(model => (
                <div key={model} className="model-item">
                  <input
                    type="checkbox"
                    id={`ml-${model}`}
                    checked={selectedMLModels.includes(model)}
                    onChange={() => toggleMLModel(model)}
                  />
                  <label htmlFor={`ml-${model}`}>{model}</label>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Canvas */}
      <div className="canvas-wrapper" style={{ pointerEvents: isProcessing || isClassifying ? 'none' : 'auto' }}>
        <svg
          ref={canvasRef}
          className="drawing-canvas"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          {/* Render completed strokes */}
          {completedStrokes.map((stroke, index) => (
            <path
              key={index}
              d={stroke.path}
              fill={stroke.color || '#000000'}
              fillOpacity={stroke.opacity !== undefined ? stroke.opacity : 1.0}
              stroke="none"
            />
          ))}

          {/* Render current stroke preview */}
          {currentPathData && (
            <path
              d={currentPathData}
              fill={currentStrokeColor}
              fillOpacity={strokeOpacity}
              stroke="none"
            />
          )}
        </svg>
      </div>

      {/* Classification Results Modal */}
      {showResultsModal && classificationResults && (
        <div className="results-modal-overlay" onClick={() => setShowResultsModal(false)}>
          <div className="results-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowResultsModal(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            
            <div className="modal-header">
              <h2>Classification Results</h2>
              <div className="modal-actions">
                <button className="btn-copy" onClick={handleCopyResults}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Copy Results
                </button>
                <button className="btn-download" onClick={handleDownloadResults}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Download ZIP
                </button>
              </div>
            </div>

            <div className="results-content">
              {Object.entries(classificationResults.results).map(([modelName, result]) => (
                <div key={modelName} className="result-item">
                  <h3 className="model-name">{modelName}</h3>
                  <div className="result-details">
                    <div className="prediction">
                      <span className="label">Prediction:</span>
                      <span className="value">{result.prediction}</span>
                    </div>
                    <div className="confidence">
                      <span className="label">Confidence:</span>
                      <span className="value">{(result.confidence * 100).toFixed(2)}%</span>
                    </div>
                    {result.probabilities && Object.keys(result.probabilities).length > 0 && (
                      <div className="probabilities">
                        <span className="label">All Probabilities:</span>
                        <div className="prob-list">
                          {Object.entries(result.probabilities)
                            .sort(([, a], [, b]) => b - a)
                            .map(([className, prob]) => (
                              <div key={className} className="prob-item">
                                <span className="class-name">{className}:</span>
                                <span className="prob-value">{(prob * 100).toFixed(2)}%</span>
                                <div className="prob-bar">
                                  <div 
                                    className="prob-bar-fill" 
                                    style={{ width: `${prob * 100}%` }}
                                  ></div>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Drawing;

